import { useNavigate, Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CRMPageHeader } from '@/components/crm/CRMPageHeader';
import { useCRM } from '@/hooks/useCRM';
import { useAuth } from '@/context/AuthContext';
import type { Contact } from '@/types/contacts';
import {
  Users,
  Briefcase,
  DollarSign,
  Target,
  Activity,
  Home,
  Search,
  Plus,
  CheckSquare,
  Bell,
} from 'lucide-react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from 'recharts';

// Stage colors for pie chart - bright vibrant palette (visible in both light/dark)
const STAGE_COLORS: Record<string, string> = {
  new: '#3B82F6',           // Bright Blue 500
  qualified: '#0EA5E9',     // Sky 500 (Cyan)
  proposal: '#F59E0B',      // Bright Amber 500
  negotiation: '#A855F7',   // Bright Purple 500
  closed_won: '#10B981',    // Bright Emerald 500
  closed_lost: '#EF4444',   // Bright Red 500
};

const STAGE_NAMES: Record<string, string> = {
  new: 'New',
  qualified: 'Qualified',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  closed_won: 'Won',
  closed_lost: 'Lost',
};

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ElementType;
  onClick?: () => void;
}

function StatCard({ title, value, icon: Icon, delay = 0, onClick }: StatCardProps & { delay?: number }) {
  return (
    <Card 
      className={cn(
        "border-border transition-shadow duration-200",
        onClick && "cursor-pointer hover:shadow-md",
        !onClick && "hover:shadow-sm"
      )}
      style={{ '--delay': `${delay}ms` } as React.CSSProperties}
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className={cn(
              "text-xs font-medium uppercase tracking-wide text-muted-foreground"
            )}>{title}</p>
            <h3 className="text-2xl font-semibold tracking-tight text-foreground">{value}</h3>
          </div>
          <div className={cn(
            "h-11 w-11 rounded-2xl flex items-center justify-center transition-colors",
            onClick ? "bg-[#2B7FFF]/15" : "bg-[#2B7FFF]/10"
          )}>
            <Icon className="h-5 w-5 text-[#2B7FFF]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function CRMDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { stats: _stats, isLoading, error, contacts } = useCRM();
  const [hoveredStage, setHoveredStage] = useState<string | null>(null);

  // Log errors for monitoring
  if (error) {
    logger.error('Dashboard load failed', new Error(error), {
      component: 'CRMDashboard',
      timestamp: new Date().toISOString(),
    });
  }

  // Add analytics for stat card clicks
  const handleStatClick = (stat: string) => {
    logger.debug('Stat card clicked', { stat, timestamp: new Date().toISOString() });
  };

  // Calculate real pipeline data from contacts
  const pipelineData = useMemo(() => {
    const data = [
      { name: 'New', stage: 'new', value: 0, color: STAGE_COLORS.new },
      { name: 'Qualified', stage: 'qualified', value: 0, color: STAGE_COLORS.qualified },
      { name: 'Proposal', stage: 'proposal', value: 0, color: STAGE_COLORS.proposal },
      { name: 'Negotiation', stage: 'negotiation', value: 0, color: STAGE_COLORS.negotiation },
      { name: 'Won', stage: 'closed_won', value: 0, color: STAGE_COLORS.closed_won },
      { name: 'Lost', stage: 'closed_lost', value: 0, color: STAGE_COLORS.closed_lost },
    ];

    contacts.forEach((contact: Contact) => {
      const item = data.find(d => d.stage === contact.stage);
      if (item) item.value += 1;
    });

    return data.filter(d => d.value > 0);
  }, [contacts]);

  // Get recent contacts (last 5 added)
  const recentContacts = useMemo(() => {
    return [...contacts]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);
  }, [contacts]);

  // Calculate active deals (contacts in qualified, proposal, or negotiation)
  const activeDeals = useMemo(() => {
    return contacts.filter((c: Contact) => ['qualified', 'proposal', 'negotiation'].includes(c.stage)).length;
  }, [contacts]);

  // Calculate total revenue (sum of value for closed_won)
  const totalRevenue = useMemo(() => {
    return contacts
        .filter((c: Contact) => c.stage === 'closed_won')
        .reduce((sum: number, c: Contact) => sum + (c.value || 0), 0);
  }, [contacts]);

  // Calculate conversion rate (closed_won / total * 100)
  const conversionRate = useMemo(() => {
    if (contacts.length === 0) return 0;
    const won = contacts.filter((c: Contact) => c.stage === 'closed_won').length;
    return Math.round((won / contacts.length) * 100);
  }, [contacts]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
          <Activity className="h-8 w-8 text-red-500" />
        </div>
        <h3 className="text-xl font-semibold">Unable to load dashboard</h3>
        <p className="text-sm text-muted-foreground max-w-md text-center">
          We're having trouble retrieving your dashboard data. Please try again or contact support if the problem persists.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
          <Button onClick={() => navigate('/search')}>
            Go to Search
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with back to main site */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Link to="/search" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <Home className="h-4 w-4" />
            <span className="text-sm">Back to Investor Search</span>
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/crm/contacts" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <Search className="h-4 w-4" />
            <span className="text-sm">Find Investors</span>
          </Link>
        </div>

        <CRMPageHeader
          title="Dashboard"
          description={`Welcome back, ${user?.name?.split(' ')[0] || 'there'}! Here's what's happening with your contacts.`}
          actions={(
            <>
              <Button 
                variant="outline"
                onClick={() => navigate('/search')}
              >
                <Search className="h-4 w-4 mr-2" />
                Find Investors
              </Button>
              <Button 
                onClick={() => navigate('/crm/contacts')}
                className="bg-[#2B7FFF] hover:bg-[#2B7FFF]/90"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Contact
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/crm/reminders')}
              >
                <Bell className="h-4 w-4 mr-2" />
                Tasks & Reminders
              </Button>
            </>
          )}
        />
      </div>

      {/* Stats Grid - Real Data Only */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Contacts"
          value={isLoading ? '-' : String(contacts.length)}
          icon={Users}
          delay={100}
          onClick={() => {
            handleStatClick('total_contacts');
            navigate('/crm/contacts?view=list');
          }}
        />
        <StatCard
          title="Active Deals"
          value={isLoading ? '-' : String(activeDeals)}
          icon={Briefcase}
          delay={200}
          onClick={() => {
            handleStatClick('active_deals');
            navigate('/crm/deals?status=active');
          }}
        />
        <StatCard
          title="Total Revenue"
          value={isLoading ? '$0' : `$${totalRevenue.toLocaleString()}`}
          icon={DollarSign}
          delay={300}
          onClick={() => {
            handleStatClick('total_revenue');
            navigate('/crm/deals?status=won');
          }}
        />
        <StatCard
          title="Conversion Rate"
          value={isLoading ? '-' : `${conversionRate}%`}
          icon={Target}
          delay={400}
          onClick={() => {
            handleStatClick('conversion_rate');
            navigate('/crm/analytics?view=funnel');
          }}
        />
      </div>

      
      {/* Charts Row */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Pipeline Chart - Real Data */}
        <Card className="border-border transition-shadow duration-200 hover:shadow-md" style={{ '--delay': '500ms' } as React.CSSProperties}>
          <CardHeader>
            <CardTitle>Pipeline Distribution</CardTitle>
            <CardDescription>Contacts by stage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px] flex items-center justify-center">
              {contacts.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const percentage = Math.round((data.value / contacts.length) * 100);
                          return (
                            <div className="bg-slate-900 text-white px-4 py-3 rounded-md shadow-xl">
                              <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">
                                {data.name}
                              </p>
                              <div className="flex items-baseline gap-2">
                                <span className="text-xl font-semibold">{data.value}</span>
                                <span className="text-sm text-slate-400">({percentage}%)</span>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Pie
                      data={pipelineData}
                      cx="50%"
                      cy="50%"
                      innerRadius={75}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="none"
                      cornerRadius={8}
                      onMouseEnter={(_, index) => setHoveredStage(pipelineData[index]?.stage || null)}
                      onMouseLeave={() => setHoveredStage(null)}
                      onClick={(_, index) => {
                        const stage = pipelineData[index]?.stage;
                        if (stage) navigate(`/crm/contacts?view=list&stage=${stage}`);
                      }}
                      className="cursor-pointer"
                    >
                      {pipelineData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.color} 
                          opacity={hoveredStage === null || hoveredStage === entry.stage ? 1 : 0.25}
                          style={{ 
                            transition: 'opacity 0.2s ease',
                            filter: hoveredStage === entry.stage ? 'brightness(1.1)' : 'none'
                          }}
                        />
                      ))}
                    </Pie>
                    {/* Center Label */}
                    <text
                      x="50%"
                      y="45%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="fill-muted-foreground text-xs uppercase tracking-widest"
                    >
                      Total
                    </text>
                    <text
                      x="50%"
                      y="55%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="fill-foreground text-2xl font-bold"
                    >
                      {contacts.length}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-muted-foreground text-center">
                  <p className="text-lg font-medium">No contacts yet</p>
                  <p className="text-sm mt-1">Add contacts to see your pipeline</p>
                  <Button 
                    className="mt-4 bg-[#2B7FFF]" 
                    onClick={() => navigate('/search')}
                  >
                    Find Investors
                  </Button>
                </div>
              )}
            </div>
            {contacts.length > 0 && (
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4 pt-4 border-t border-slate-100">
                {pipelineData.map((stage) => (
                  <div 
                    key={stage.name} 
                    className="flex items-center gap-2 cursor-pointer group"
                    onMouseEnter={() => setHoveredStage(stage.stage)}
                    onMouseLeave={() => setHoveredStage(null)}
                    onClick={() => navigate(`/crm/contacts?view=list&stage=${stage.stage}`)}
                  >
                    <div 
                      className="w-3 h-3 rounded-sm transition-all"
                      style={{ 
                        backgroundColor: stage.color,
                        opacity: hoveredStage === null || hoveredStage === stage.stage ? 1 : 0.3
                      }}
                    />
                    <span 
                      className="text-sm text-muted-foreground group-hover:text-slate-900 transition-colors"
                      style={{ opacity: hoveredStage === null || hoveredStage === stage.stage ? 1 : 0.4 }}
                    >
                      {stage.name}
                    </span>
                    <span 
                      className="text-sm font-medium text-slate-900"
                      style={{ opacity: hoveredStage === null || hoveredStage === stage.stage ? 1 : 0.4 }}
                    >
                      {stage.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity - Real Data */}
        <Card className="border-border transition-shadow duration-200 hover:shadow-md" style={{ '--delay': '600ms' } as React.CSSProperties}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Contacts</CardTitle>
              <CardDescription>Recently added to your network</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/crm/contacts?view=list')}>
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {recentContacts.length > 0 ? (
              <div className="space-y-4">
                {recentContacts.map((contact) => {
                  const initials = (contact.name || 'U')
                    .split(' ')
                    .map((n: string) => n[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase();
                  const displayCompany = contact.company || contact.email || 'Contact';

                  return (
                    <div
                      key={contact.id}
                      className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/crm/contacts/${contact.id}`)}
                    >
                      <div className="h-10 w-10 rounded-lg bg-[#2B7FFF]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-[#2B7FFF]">{initials}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{contact.name || 'Unnamed'}</p>
                        <p className="text-sm text-muted-foreground truncate">{displayCompany}</p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {STAGE_NAMES[contact.stage] || contact.stage}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-lg font-medium">No contacts yet</p>
                <p className="text-sm mt-1">Go to Find Investors to add contacts</p>
                <Button 
                  className="mt-4 bg-[#2B7FFF]" 
                  onClick={() => navigate('/search')}
                >
                  Find Investors
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default CRMDashboard;

